import { useState } from "react";
import { Beaker, RefreshCw, MessageSquarePlus, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useToast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/lib/edge-functions";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DemoControlsProps {
  /** Thread ID to use when simulating a vendor reply. If omitted, the simulate
   *  button is hidden. */
  threadId?: string;
  /** Called after a successful seed/reset so the parent can refresh data. */
  onSeedSuccess?: () => void;
  /** Called after a successful simulate so the parent can refresh data. */
  onSimulateSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DemoControls({ threadId, onSeedSuccess, onSimulateSuccess }: DemoControlsProps) {
  const { isEnabled, loading: flagsLoading } = useFeatureFlags();
  const { toast } = useToast();

  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [simulating, setSimulating] = useState(false);

  const demoEnabled = isEnabled("vendor_negotiator_demo");

  // While flags are loading, render nothing to avoid flash
  if (flagsLoading) return null;
  if (!demoEnabled) return null;

  async function handleSeed() {
    setSeeding(true);
    try {
      await invokeEdgeFunction("seed-negotiation-demo", { reset: false });
      toast({ title: "Demo data seeded" });
      onSeedSuccess?.();
    } catch (err) {
      toast({
        title: "Failed to seed demo data",
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setSeeding(false);
    }
  }

  async function handleReset() {
    setResetting(true);
    try {
      await invokeEdgeFunction("seed-negotiation-demo", { reset: true });
      toast({ title: "Demo data reset" });
      onSeedSuccess?.();
    } catch (err) {
      toast({
        title: "Failed to reset demo data",
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setResetting(false);
    }
  }

  async function handleSimulate() {
    if (!threadId) return;
    setSimulating(true);
    try {
      await invokeEdgeFunction("negotiation-simulate-reply", { thread_id: threadId });
      toast({ title: "Vendor reply simulated" });
      onSimulateSuccess?.();
    } catch (err) {
      toast({
        title: "Failed to simulate reply",
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setSimulating(false);
    }
  }

  return (
    <Card className="border-warning/40 bg-warning/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-warning flex items-center gap-2">
          <Beaker className="w-4 h-4" />
          Demo Controls
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-3">
          These controls are only visible when the{" "}
          <code className="text-xs bg-muted/50 px-1 rounded">vendor_negotiator_demo</code> feature
          flag is enabled.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-xs border-warning/40 text-warning hover:bg-warning/10"
            onClick={handleSeed}
            disabled={seeding || resetting || simulating}
            title="Seed demo negotiation data"
            aria-label="Seed demo data"
          >
            {seeding ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Beaker className="w-3.5 h-3.5" />
            )}
            Seed demo data
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-xs border-warning/40 text-warning hover:bg-warning/10"
            onClick={handleReset}
            disabled={seeding || resetting || simulating}
            title="Reset demo data to initial state"
            aria-label="Reset demo data"
          >
            {resetting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Reset demo data
          </Button>

          {threadId && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs border-warning/40 text-warning hover:bg-warning/10"
              onClick={handleSimulate}
              disabled={seeding || resetting || simulating}
              title="Simulate a vendor reply for this thread"
              aria-label="Simulate vendor reply"
            >
              {simulating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <MessageSquarePlus className="w-3.5 h-3.5" />
              )}
              Simulate vendor reply
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
